import { LegacyRef, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import {
  Button,
  ButtonGroup,
  Card,
  Input,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Spinner,
  Tabs,
  TabsContent,
  TabsItem,
  TabsList,
  toast,
} from 'keep-react'

import { Wrapper } from 'components'
import Quill from 'quill'
import { Editor } from 'components/QuillEditor'
import { ArrowLeft, FloppyDisk, Trash } from 'phosphor-react'
import { RouteNamesAuth } from 'routes/enum'
import { Webisode } from 'api/types/webisode'
import { webisodeService } from 'api/services/webisodes'

// form validation schema
const validationSchema = Yup.object().shape({
  categoryId: Yup.number(),
  status: Yup.number().oneOf([0, 1]),
  hasSimulation: Yup.boolean(),
  productFiles: Yup.mixed().nullable(),
})

export const BackOfficeWebisode = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  //first load and is api being called
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isApiBeingCalled, setIsApiBeingCalled] = useState<boolean>(false)

  //loaded webisode data
  const [data, setData] = useState<Webisode | null>(null)

  // Quill States
  const [content, setContent] = useState<string>('')

  // Modal Control
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  // Save Button Active?
  const [hasChanges, setHasChanges] = useState<boolean>(false)

  // Editor Ref
  const quillContentRef = useRef(null)

  //get webisode details
  const getWebisode = async () => {
    setIsLoading(true)
    try {
      const response = (await webisodeService.getWebisode({
        id: id as string,
      })) as Webisode
      setData(response as Webisode)
      formik.setFieldValue('id', response.id)
      formik.setFieldValue('title', response.title)
      setContent(response.content ?? '')
      formik.setFieldValue('description', response.description)
      formik.setFieldValue('label', response.label)
      formik.setFieldValue('link', response.link)

      setIsLoading(false)
    } catch (error) {
      toast.error('Não foi possível carregar o webisode.')
    }
  }

  // using formik to handle form state
  const formik = useFormik({
    initialValues: {
      id: id,
      title: '',
      description: '',
      label: '',
      link: '',
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async values => {
      if (!id) return
      const newValues = {
        id: values.id,
        title: values.title,
        description: values.description,
        content: content,
        label: values.label,
        link: values.link,
      } as Webisode

      try {
        setIsApiBeingCalled(true)
        await webisodeService.saveWebisode(newValues)

        toast.success('Webisode atualizado com sucesso.')
        setHasChanges(false)
      } catch (error) {
        toast.error('Erro ao atualizar webisode.')
      } finally {
        setIsApiBeingCalled(false)
      }
    },
  })

  // delete selected webisode
  const deleteWebisode = async () => {
    if (!data) return
    try {
      await webisodeService.deleteWebisode({
        id: String(data.id),
      })
      setShowDeleteModal(false)
      toast.success('Webisode apagado com sucesso.')
      navigate(RouteNamesAuth.BACKOFFICE_WEBISODES)
    } catch (error) {
      toast.error('Não foi possível apagar o webisode.')
    }
  }

  // initial data fetch
  useEffect(() => {
    getWebisode()
  }, [])

  // track changes to enable save button
  useEffect(() => {
    setHasChanges(true)
  }, [content, formik.values])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        {isLoading || !data ? (
          <Card className="w-full max-w-full p-4 md:p-6">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : (
          <>
            <Button
              variant="outline"
              color="secondary"
              className="mb-6"
              onClick={() => navigate(RouteNamesAuth.BACKOFFICE_WEBISODES)}
            >
              <ArrowLeft size={16} /> Webisodes
            </Button>
            <Card className="w-full max-w-full p-4 md:p-6">
              <h1 className="pb-10 text-xxxl font-medium text-metal-900 flex items-center justify-between">
                <span></span>
                <>
                  <ButtonGroup>
                    <Button
                      type="submit"
                      position="start"
                      color="secondary"
                      variant="default"
                      disabled={!hasChanges || isApiBeingCalled}
                      className="gap-2"
                      onClick={() => {
                        formik.handleSubmit()
                      }}
                    >
                      <FloppyDisk size={16} />
                      Gravar alterações
                    </Button>

                    <Button
                      position="end"
                      variant="default"
                      color="error"
                      onClick={() => {
                        setShowDeleteModal(true)
                      }}
                      disabled={isApiBeingCalled}
                      className="gap-2"
                    >
                      <Trash size={16} />
                      Apagar Webisode
                    </Button>
                  </ButtonGroup>
                </>
              </h1>

              <Tabs defaultValue="general">
                <TabsList>
                  <TabsItem value="general">Geral</TabsItem>
                  <TabsItem value="documents">Documentos</TabsItem>
                </TabsList>
                <TabsContent value="general" className="p-4 md:p-6">
                  <div className="space-y-8">
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Título
                      </h3>
                      <Input
                        name="title"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Descrição / Subtítulo
                      </h3>
                      <Input
                        name="description"
                        value={formik.values.description}
                        onChange={formik.handleChange}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Conteúdo
                      </h3>
                      <div>
                        <Editor
                          ref={
                            quillContentRef as unknown as
                              | LegacyRef<Quill>
                              | undefined
                          }
                          defaultValue={content}
                          setContent={setContent}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Label do botão
                      </h3>
                      <Input
                        name="label"
                        value={formik.values.label}
                        onChange={formik.handleChange}
                      />
                    </div>
                    <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                      <h3 className="text-body-4 font-bold text-metal-900">
                        Link da campanha
                      </h3>
                      <Input
                        name="link"
                        value={formik.values.link}
                        onChange={formik.handleChange}
                      />
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="documents" className="p-4 md:p-6">
                  <p>Conteúdo dos Documentos do Webisode</p>
                </TabsContent>
              </Tabs>
            </Card>
          </>
        )}
      </Wrapper>

      <Modal open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Apagar Webisode</ModalTitle>
            <ModalDescription>
              Quer apagar este webisode?
              <br />
              <br />
              <strong className="text-error-500">
                Todos os dados deste webisode irão ser perdidos.
              </strong>
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" onClick={() => deleteWebisode()}>
              Apagar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
